import type { EmailSender } from '@blogforge/shared';

// ─────────────────────────────────────────────────────────────────
// Sender domain facts — which domain a sender actually sends as, and the
// DNS evidence a receiving mailbox provider looks for before it trusts it.
//
// This module is the UI's stand-in for a DNS-checking service. The records,
// the statuses and the "what to do next" copy are SAMPLE DATA derived
// deterministically from the sender (same sender in ⇒ same report out), so
// the Sender view page can be built, reviewed and tested before the server
// grows a real resolver. Nothing here touches the network, and no server
// route backs it yet.
//
// Two kinds of domain live behind this file, and they deserve very different
// answers:
//
//   · A domain the customer owns (acme.com). BlogForger can prove they own
//     it — a TXT record only they can publish — and can then tell them
//     exactly which SPF / DKIM / DMARC records to add or mend.
//   · A shared consumer mailbox (gmail.com, outlook.com…). There is no zone
//     to edit: the records belong to Google or Microsoft. Ownership can never
//     be proven, so the useful thing to do is report the domain's *mail
//     health* — what the public DNS says about it right now — and be honest
//     about which findings are not the user's to fix.
// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────

export type DnsRecordType = 'TXT' | 'CNAME' | 'MX' | 'PTR';

/** One record the user may have to add, or the one that is already answering. */
export interface DnsRecord {
  type: DnsRecordType;
  /** The host as a DNS panel asks for it: `@`, `_dmarc`, `bf1._domainkey`. */
  host: string;
  /** The same host spelled in full — what a resolver was actually asked for. */
  fqdn: string;
  value: string;
  /** TTL as a DNS panel labels it. */
  ttl: string;
}

export type DnsCheckKind =
  | 'ownership'
  | 'spf'
  | 'dkim'
  | 'dmarc'
  | 'mx'
  | 'mta-sts'
  | 'blocklist'
  | 'ptr';

/**
 * `ok`            — found, correct, nothing to do.
 * `propagating`   — known to be right, but public DNS has not caught up yet.
 * `warn`          — works, but leaves deliverability on the table.
 * `action`        — broken or absent; mail is at risk until it is fixed.
 * `optional`      — a recommended extra, never a blocker.
 */
export type DnsCheckStatus = 'ok' | 'propagating' | 'warn' | 'action' | 'optional';

export interface DnsFix {
  /** One line the user can act on without reading the rest. */
  summary: string;
  /** Ordered, concrete steps — where to click, then what to paste. */
  steps: string[];
  /** A companion record the fix needs (a DMARC reporting address, say). */
  extra: DnsRecord | null;
  /** What to do instead when the record is not the user's to change. */
  alternative: string | null;
}

export interface DnsCheck {
  /** Stable id — used as a React key and asserted in tests. */
  id: string;
  kind: DnsCheckKind;
  /** Heading a non-specialist can read. */
  label: string;
  /** The question this check answers, in one line. */
  question: string;
  status: DnsCheckStatus;
  /** The lookup that produced `found`. */
  lookup: { type: DnsRecordType; host: string; name: string };
  /** What DNS answers today (sample). `null` when nothing was found. */
  found: string | null;
  /** The record that should exist. `null` when the user cannot publish one. */
  expected: DnsRecord | null;
  /** False → good practice, not a precondition for mail landing. */
  required: boolean;
  /** What this record buys the sender, in plain words. */
  why: string;
  /** Set only while there is something to do. */
  fix: DnsFix | null;
}

export interface DomainVerification {
  domain: string;
  /**
   * `unverifiable` is the shared-mailbox case: there is no zone the user can
   * write to, so no amount of retrying will produce a verified domain.
   */
  status: 'verified' | 'pending' | 'action_required' | 'unverifiable';
  /** When the report below was produced. */
  checkedAt: string;
  /** When BlogForger last confirmed ownership; `null` until it has. */
  verifiedAt: string | null;
  /** The value the ownership TXT record has to carry. */
  token: string;
  /** Full name of the ownership record, e.g. `_blogforger.acme.com`. */
  ownershipHost: string;
  /** Required checks still blocking verification, worst first. */
  blockers: DnsCheck[];
  /** Every check, worst first. */
  checks: DnsCheck[];
}

export interface MailHealth {
  domain: string;
  /** True when the domain is a shared mailbox host rather than the user's own. */
  publicHost: boolean;
  /** Recognised mailbox provider ("Google"), when the domain is a known one. */
  provider: string | null;
  /** What the sender hands mail to: an SMTP host, or the Brevo API. */
  relay: string;
  checkedAt: string;
  /** Resolvers the (stubbed) check says it queried. */
  resolvers: string[];
  score: number;
  grade: 'A' | 'B' | 'C' | 'D';
  /** One-line verdict for the whole domain. */
  verdict: string;
  checks: DnsCheck[];
  /** The checks the user has to do something about, worst first. */
  fixes: DnsCheck[];
}

export interface CheckOptions {
  /** Add the relay-side checks (blocklist, reverse DNS). */
  includeRelay?: boolean;
}

// ─────────────────────────────────────────────────────────────────
// Which domain is this sender sending as?
// ─────────────────────────────────────────────────────────────────

/**
 * Consumer mailbox domains. Mail sent as one of these cannot be authenticated
 * by its sender — the DNS zone belongs to the provider — which is exactly why
 * the view page offers a mail-health report for them instead of a domain
 * verification flow.
 */
export const PUBLIC_MAILBOX_DOMAINS: readonly string[] = [
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'yahoo.co.uk',
  'ymail.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'zoho.com',
  'fastmail.com',
  'gmx.com',
  'gmx.net',
  'web.de',
  'mail.com',
  'yandex.com',
  'yandex.ru',
  'mail.ru',
  'qq.com',
  '163.com',
  '126.com',
  'naver.com',
  'tutanota.com',
  'hey.com',
  'rediffmail.com',
  'comcast.net',
  'verizon.net',
  'att.net',
  'sbcglobal.net',
  'btinternet.com',
  'orange.fr',
  'laposte.net',
  'libero.it',
  'seznam.cz',
  'wp.pl',
];

/** Two-label public suffixes the naive registrable-domain split gets wrong. */
const MULTI_LABEL_SUFFIXES: readonly string[] = [
  'co.uk',
  'org.uk',
  'ac.uk',
  'gov.uk',
  'co.za',
  'co.nz',
  'com.au',
  'net.au',
  'co.jp',
  'co.in',
  'co.kr',
  'com.br',
  'com.mx',
  'com.tr',
  'com.ng',
  'com.gh',
  'com.ke',
  'co.ke',
];

/** SMTP host prefixes that say nothing about the sending domain. */
const RELAY_HOST_PREFIXES: readonly string[] = [
  'smtp',
  'smtp-mail',
  'smtp-relay',
  'mail',
  'email',
  'send',
  'relay',
  'mailout',
  'outbound',
];

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/** The sender's stored, non-secret config, defensively parsed. */
export function senderData(sender: EmailSender): Record<string, unknown> {
  const data = sender.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return {};
}

export function looksLikeEmail(value: string | null): value is string {
  return value !== null && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * The address recipients see as "From". SMTP senders fall back to the SMTP
 * username (the server does the same), Brevo senders to their sender address.
 */
export function senderFromAddress(sender: EmailSender): string | null {
  const data = senderData(sender);
  if (sender.senderType === 'brevo_api') {
    const from = str(data.fromEmail);
    return looksLikeEmail(from) ? from : null;
  }
  const from = str(data.from);
  if (looksLikeEmail(from)) return from;
  const user = str(data.user);
  return looksLikeEmail(user) ? user : null;
}

/** `hello@acme.com` → `acme.com`. Null when the sender has no address yet. */
export function addressDomain(address: string | null): string | null {
  if (!looksLikeEmail(address)) return null;
  const domain = address.slice(address.lastIndexOf('@') + 1).toLowerCase();
  return domain.replace(/\.+$/, '') || null;
}

/** `a.b.acme.co.uk` → `acme.co.uk` — enough to drive a record plan. */
export function registrableDomain(host: string): string {
  const labels = host.toLowerCase().replace(/\.+$/, '').split('.').filter(Boolean);
  if (labels.length <= 2) return labels.join('.');
  const lastTwo = labels.slice(-2).join('.');
  if (MULTI_LABEL_SUFFIXES.includes(lastTwo) && labels.length >= 3) {
    return labels.slice(-3).join('.');
  }
  return lastTwo;
}

/**
 * The domain this sender sends as.
 *
 * The From address is the authority. When a sender has no address yet (a
 * half-configured SMTP row), the SMTP host is the best hint left: strip the
 * relay-ish prefix and keep the registrable domain, so `smtp.gmail.com`
 * answers `gmail.com` rather than `smtp.gmail.com`.
 */
export function senderDomain(sender: EmailSender): string | null {
  const from = addressDomain(senderFromAddress(sender));
  if (from) return from;

  const data = senderData(sender);
  const host = str(data.host);
  if (!host || !host.includes('.')) return null;

  const labels = host.toLowerCase().split('.').filter(Boolean);
  if (labels.length > 2 && RELAY_HOST_PREFIXES.includes(labels[0] ?? '')) {
    labels.shift();
  }
  return registrableDomain(labels.join('.'));
}

/** True for gmail.com and friends — domains nobody can publish records for. */
export function isPublicMailboxDomain(domain: string | null): boolean {
  if (!domain) return false;
  const host = domain.toLowerCase();
  return PUBLIC_MAILBOX_DOMAINS.some((entry) => host === entry || host.endsWith(`.${entry}`));
}

export function isPublicMailboxSender(sender: EmailSender): boolean {
  return isPublicMailboxDomain(senderDomain(sender));
}

/** What the sender hands mail to — shown next to "via" in the report header. */
export function senderRelay(sender: EmailSender): string {
  if (sender.senderType === 'brevo_api') return 'Brevo API';
  const data = senderData(sender);
  const host = str(data.host);
  const port = data.port === undefined ? null : String(data.port);
  if (!host) return 'SMTP (not configured)';
  return port ? `${host}:${port}` : host;
}

// ─────────────────────────────────────────────────────────────────
// Deterministic sample data
//
// Every sender gets its own report that never changes between reloads: the
// seed is the sender id plus its domain, and the pseudo-random stream is
// fixed-length per sender, so adding a field later cannot reshuffle the
// others.
// ─────────────────────────────────────────────────────────────────

/** FNV-1a — small, fast, and stable across engines. */
export function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/** mulberry32 — one uniform value per call, from a 32-bit seed. */
export function rng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The ownership-record value: `blogforger-verify=<26 lowercase chars>`. */
export function verificationToken(seed: number): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789';
  const rand = rng(seed ^ 0x9e3779b9);
  let out = '';
  for (let i = 0; i < 26; i += 1) {
    out += alphabet.charAt(Math.floor(rand() * alphabet.length));
  }
  return out;
}

function record(
  domain: string,
  type: DnsRecordType,
  host: string,
  value: string,
  ttl = 'Auto',
): DnsRecord {
  return {
    type,
    host,
    fqdn: host === '@' ? domain : `${host}.${domain}`,
    value,
    ttl,
  };
}

function daysAgo(now: Date, days: number, hour = 9): string {
  const date = new Date(now.getTime() - days * 86_400_000);
  date.setHours(hour, 0, 0, 0);
  return date.toISOString();
}

// ─────────────────────────────────────────────────────────────────
// Known mailbox providers
//
// Public hosts are checked, not configured: all a report can honestly do is
// say what the domain publishes today. These are the well-known ones with
// their real-world record shapes; anything else falls back to a plausible
// provider-shaped answer.
// ─────────────────────────────────────────────────────────────────

interface ProviderFacts {
  provider: string;
  spf: string;
  dkimSelector: string;
  dmarc: string;
  mx: string;
}

const MICROSOFT: ProviderFacts = {
  provider: 'Microsoft',
  spf: 'v=spf1 include:spf.protection.outlook.com -all',
  dkimSelector: 'selector1',
  dmarc: 'v=DMARC1; p=none;',
  mx: '10 outlook-com.olc.protection.outlook.com',
};

const YAHOO: ProviderFacts = {
  provider: 'Yahoo',
  spf: 'v=spf1 redirect=_spf.mail.yahoo.com',
  dkimSelector: 's1024',
  dmarc: 'v=DMARC1; p=reject; pct=100',
  mx: '10 mta5.am0.yahoodns.net',
};

const PROVIDER_FACTS: Record<string, ProviderFacts> = {
  'gmail.com': {
    provider: 'Google',
    spf: 'v=spf1 redirect=_spf.google.com',
    dkimSelector: 'google',
    dmarc: 'v=DMARC1; p=none; sp=quarantine; pct=100',
    mx: '10 gmail-smtp-in.l.google.com',
  },
  'googlemail.com': {
    provider: 'Google',
    spf: 'v=spf1 redirect=_spf.google.com',
    dkimSelector: 'google',
    dmarc: 'v=DMARC1; p=none; sp=quarantine; pct=100',
    mx: '10 gmail-smtp-in.l.google.com',
  },
  'outlook.com': MICROSOFT,
  'hotmail.com': MICROSOFT,
  'live.com': MICROSOFT,
  'msn.com': MICROSOFT,
  'yahoo.com': YAHOO,
  'yahoo.co.uk': YAHOO,
  'ymail.com': YAHOO,
  'aol.com': YAHOO,
  'icloud.com': {
    provider: 'Apple iCloud',
    spf: 'v=spf1 include:icloud.com ~all',
    dkimSelector: 'sig1',
    dmarc: 'v=DMARC1; p=quarantine; pct=100',
    mx: '10 mx01.mail.icloud.com',
  },
  'me.com': {
    provider: 'Apple iCloud',
    spf: 'v=spf1 include:icloud.com ~all',
    dkimSelector: 'sig1',
    dmarc: 'v=DMARC1; p=quarantine; pct=100',
    mx: '10 mx01.mail.icloud.com',
  },
  'proton.me': {
    provider: 'Proton Mail',
    spf: 'v=spf1 include:_spf.protonmail.ch ~all',
    dkimSelector: 'protonmail',
    dmarc: 'v=DMARC1; p=quarantine; pct=100',
    mx: '10 mail.protonmail.ch',
  },
  'zoho.com': {
    provider: 'Zoho Mail',
    spf: 'v=spf1 include:zoho.com ~all',
    dkimSelector: 'zoho',
    dmarc: 'v=DMARC1; p=none;',
    mx: '10 mx.zoho.com',
  },
  'fastmail.com': {
    provider: 'Fastmail',
    spf: 'v=spf1 include:spf.messagingengine.com ~all',
    dkimSelector: 'fm1',
    dmarc: 'v=DMARC1; p=quarantine; pct=100',
    mx: '10 in1-smtp.messagingengine.com',
  },
};

function titleCase(value: string): string {
  return value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);
}

function providerFacts(domain: string): ProviderFacts {
  const known = PROVIDER_FACTS[domain];
  if (known) return known;
  const brand = domain.split('.')[0] ?? domain;
  return {
    provider: titleCase(brand),
    spf: `v=spf1 include:_spf.${domain} ~all`,
    dkimSelector: brand.slice(0, 8),
    dmarc: 'v=DMARC1; p=none;',
    mx: `10 mx.${domain}`,
  };
}

// ─────────────────────────────────────────────────────────────────
// The checks
// ─────────────────────────────────────────────────────────────────

/** Sort worst first, so the page can render the list as-is. */
export function sortChecks(checks: readonly DnsCheck[]): DnsCheck[] {
  const weight: Record<DnsCheckStatus, number> = {
    action: 0,
    propagating: 1,
    warn: 2,
    optional: 3,
    ok: 4,
  };
  return [...checks].sort((a, b) => weight[a.status] - weight[b.status]);
}

/**
 * Every DNS check for this sender, worst first.
 *
 * Which checks exist depends on the domain, not on the sender type: a shared
 * mailbox has records to *read*, an owned domain has records to *write*, and
 * ownership only means something in the second case.
 */
export function buildDnsChecks(sender: EmailSender, options: CheckOptions = {}): DnsCheck[] {
  const domain = senderDomain(sender) ?? '';
  const publicHost = isPublicMailboxDomain(domain);
  const includeRelay = options.includeRelay ?? publicHost;
  const seed = hashSeed(`${sender.id}|${domain}`);
  const rand = rng(seed);
  const facts = providerFacts(domain);
  const relay = senderRelay(sender);
  const relayHost = sender.senderType === 'smtp' ? str(senderData(sender).host) : null;

  const checks: DnsCheck[] = [];

  // ── Ownership (owned domains only) ─────────────────────────────
  if (!publicHost) {
    const ownershipOk = rand() < 0.55;
    const token = verificationToken(seed);
    const ownership = record(domain, 'TXT', '_blogforger', `blogforger-verify=${token}`);
    checks.push({
      id: 'ownership',
      kind: 'ownership',
      label: 'Domain ownership',
      question: 'Can you publish records for this domain?',
      status: ownershipOk ? 'ok' : 'action',
      lookup: { type: 'TXT', host: ownership.fqdn, name: ownership.fqdn },
      found: ownershipOk ? `blogforger-verify=${token}` : null,
      expected: ownership,
      required: true,
      why: 'This one record is how BlogForger knows the domain is yours. Nobody else can add it, so mail cannot be sent as your domain by someone who only guesses your name.',
      fix: ownershipOk
        ? null
        : {
            summary: 'Add one TXT record to prove you manage the domain.',
            steps: [
              'Open your DNS provider and find the zone for ' + domain + '.',
              `Add a TXT record named “${ownership.host}” with the value below.`,
              'Save it, then press “Check verification” — a new record usually shows up within minutes.',
            ],
            extra: null,
            alternative: null,
          },
    });
  }

  // ── SPF ────────────────────────────────────────────────────────
  const spfExpected = publicHost
    ? null
    : record(domain, 'TXT', '@', 'v=spf1 include:mail.blogforger.com ~all');
  const spf = (() => {
    if (publicHost) {
      return {
        status: 'ok' as DnsCheckStatus,
        found: facts.spf,
        fix: null as DnsFix | null,
      };
    }
    const roll = rand();
    if (roll < 0.62) {
      return { status: 'ok' as DnsCheckStatus, found: spfExpected?.value ?? null, fix: null };
    }
    if (roll < 0.84) {
      return {
        status: 'action' as DnsCheckStatus,
        found: null,
        fix: {
          summary: 'No SPF record — receiving servers cannot tell your mail is legitimate.',
          steps: [
            `Add a TXT record at “@” (the root of ${domain}) with the value below.`,
            'If a TXT record starting with “v=spf1” already exists, edit that one instead — a domain may only have one SPF record.',
            'Then press “Re-check DNS”.',
          ],
          extra: null,
          alternative: null,
        } satisfies DnsFix,
      };
    }
    return {
      status: 'action' as DnsCheckStatus,
      found: 'v=spf1 +all',
      fix: {
        summary:
          'Your SPF record ends in “+all”, which authorises the entire internet to send as you.',
        steps: [
          'Open the existing TXT record at “@” that starts with “v=spf1”.',
          'Replace the “+all” mechanism with “~all” (soft fail) and keep the include: entries.',
          'Do not add a second SPF record — a domain with two is treated as having none.',
        ],
        extra: null,
        alternative: null,
      } satisfies DnsFix,
    };
  })();
  checks.push({
    id: 'spf',
    kind: 'spf',
    label: 'SPF — who may send for this domain',
    question: 'Is the list of allowed senders published?',
    status: spf.status,
    lookup: { type: 'TXT', host: domain, name: domain },
    found: spf.found,
    expected: spfExpected,
    required: !publicHost,
    why: 'SPF is the list of servers allowed to send mail as your domain. Without it, providers have nothing to check and treat your mail as a guess.',
    fix: spf.fix,
  });

  // ── DKIM ───────────────────────────────────────────────────────
  // Two selectors rotate independently (BlogForger signs with both while a
  // key rotates), so they are two checks. A verified owner whose selectors are
  // not answering yet is the ordinary "DNS is still propagating" case, not an
  // error — which is why that status exists at all.
  const dkimOk = publicHost ? true : rand() < 0.68;
  const dkimBothMissing = !publicHost && !dkimOk && rand() < 0.45;
  const dkimSelectors = publicHost ? [facts.dkimSelector] : ['bf1', 'bf2'];
  dkimSelectors.forEach((selector, index) => {
    const expected = publicHost
      ? null
      : record(domain, 'CNAME', `${selector}._domainkey`, `${selector}.dkim.blogforger.com`);
    const ok = dkimOk || (!dkimBothMissing && index === 0 && !publicHost);
    const status: DnsCheckStatus = ok ? 'ok' : 'action';
    checks.push({
      id: `dkim-${selector}`,
      kind: 'dkim',
      label: `DKIM signature (${selector})`,
      question: 'Is your mail cryptographically signed?',
      status,
      lookup: {
        type: 'CNAME',
        host: expected?.fqdn ?? `${selector}._domainkey.${domain}`,
        name: `${selector}._domainkey.${domain}`,
      },
      found: ok
        ? publicHost
          ? 'v=DKIM1; k=rsa; p=MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8A…'
          : `${selector}.dkim.blogforger.com`
        : null,
      expected,
      required: !publicHost,
      why: 'A DKIM signature lets a provider prove the message really came from your domain and was not altered on the way. Signed mail is far less likely to be filtered.',
      fix: ok
        ? null
        : {
            summary: `Add the ${selector} DKIM record so BlogForger can sign your mail.`,
            steps: [
              `Add a CNAME record named “${selector}._domainkey” pointing at the target below.`,
              'Some DNS panels append the domain automatically — if so, type only “' +
                selector +
                '._domainkey”, not the whole name.',
              'Signing switches on by itself once the record resolves.',
            ],
            extra: null,
            alternative: null,
          },
    });
  });

  // ── DMARC ──────────────────────────────────────────────────────
  const dmarcExpected = publicHost
    ? null
    : record(domain, 'TXT', '_dmarc', `v=DMARC1; p=none; rua=mailto:dmarc-reports@${domain}; fo=1`);
  const dmarc = (() => {
    const published = publicHost ? facts.dmarc : null;
    if (publicHost) {
      // The policy tag alone decides this: `sp=` is the subdomain policy and
      // `pct=` is a percentage, and neither is what a receiver reads as "the"
      // policy for the domain.
      const enforcing = /\bp=(quarantine|reject)/.test(published ?? '');
      const policy = /\bp=([a-z]+)/.exec(published ?? '')?.[1] ?? 'none';
      return enforcing
        ? { status: 'ok' as DnsCheckStatus, found: published, fix: null as DnsFix | null }
        : {
            status: 'warn' as DnsCheckStatus,
            found: published,
            fix: {
              summary: `${facts.provider} publishes a monitoring-only DMARC policy (p=${policy}), so spoofed mail is not blocked.`,
              steps: [
                'Nothing here is yours to change — the record belongs to ' + facts.provider + '.',
                'Move your sending address to a domain you own, where you can publish your own DMARC policy.',
                'Until then, expect some of your mail to land in spam purely because the domain is shared.',
              ],
              extra: null,
              alternative:
                'On a domain you own, start at p=none with rua reporting, then tighten to p=quarantine once the reports look clean.',
            } satisfies DnsFix,
          };
    }
    const roll = rand();
    if (roll < 0.32) {
      return {
        status: 'ok' as DnsCheckStatus,
        found: `v=DMARC1; p=quarantine; pct=25; rua=mailto:dmarc-reports@${domain}`,
        fix: null,
      };
    }
    if (roll < 0.82) {
      return {
        status: 'warn' as DnsCheckStatus,
        found: `v=DMARC1; p=none; rua=mailto:dmarc-reports@${domain}`,
        fix: {
          summary: 'DMARC is monitoring only (p=none), so anyone can still spoof your domain.',
          steps: [
            'Leave the policy at p=none while the reports come in, and read them for a week or two.',
            'Then edit the record below and change “p=none” to “p=quarantine”.',
            'Once nothing legitimate is being quarantined, move on to “p=reject”.',
          ],
          extra: null,
          alternative: null,
        } satisfies DnsFix,
      };
    }
    return {
      status: 'action' as DnsCheckStatus,
      found: null,
      fix: {
        summary: 'No DMARC record — nobody is told what to do with mail that fails SPF and DKIM.',
        steps: [
          'Add the TXT record below at “_dmarc”.',
          'Keep the reporting address: the daily reports are how you find out what is failing.',
          'Leave the policy at p=none for a week before tightening it.',
        ],
        extra: null,
        alternative: null,
      } satisfies DnsFix,
    };
  })();
  checks.push({
    id: 'dmarc',
    kind: 'dmarc',
    label: 'DMARC — what to do with forgeries',
    question: 'Is there a published policy for mail that fails the checks?',
    status: dmarc.status,
    lookup: { type: 'TXT', host: `_dmarc.${domain}`, name: `_dmarc.${domain}` },
    found: dmarc.found,
    expected: dmarcExpected,
    required: !publicHost,
    why: 'DMARC ties SPF and DKIM together and tells providers what to do when a message fails both. Without it, forged mail carrying your address is delivered as if it came from you.',
    fix: dmarc.fix,
  });

  // ── MX (informational: it routes replies, not sends) ───────────
  const mxFound = publicHost ? facts.mx : rand() < 0.86 ? `10 mx1.${domain}` : null;
  checks.push({
    id: 'mx',
    kind: 'mx',
    label: 'MX — where replies land',
    question: 'Does your domain accept mail at all?',
    status: mxFound ? 'ok' : 'warn',
    lookup: { type: 'MX', host: domain, name: domain },
    found: mxFound,
    expected: publicHost ? null : record(domain, 'MX', '@', `10 mx1.${domain}`, '3600'),
    required: false,
    why: 'MX records decide where mail sent *to* your address goes. Without them, replies, bounces and anything you use as a reply-to address disappear silently.',
    fix: mxFound
      ? null
      : {
          summary: 'The domain cannot receive mail, so replies to your campaigns bounce.',
          steps: [
            'Point the MX record at the mailbox you actually read, or set a Reply-To address on your sends.',
            'In Send → Reply-To, enter an address on a domain that does accept mail.',
          ],
          extra: null,
          alternative: null,
        },
  });

  // ── MTA-STS / TLS reporting (recommended extra) ────────────────
  if (!publicHost) {
    checks.push({
      id: 'mta-sts',
      kind: 'mta-sts',
      label: 'MTA-STS — require TLS in transit',
      question: 'Is inbound transport security advertised?',
      status: 'optional',
      lookup: { type: 'TXT', host: `_mta-sts.${domain}`, name: `_mta-sts.${domain}` },
      found: null,
      expected: record(domain, 'TXT', '_mta-sts', `v=STSv1; id=${seed.toString(36)}`),
      required: false,
      why: 'MTA-STS asks sending servers to use TLS when they deliver to you, and to refuse rather than downgrade. It protects mail coming in, so it is optional for outbound.',
      fix: {
        summary: 'Optional hardening — publish MTA-STS and TLS reporting if you want it.',
        steps: [
          'Add the TXT record below at “_mta-sts”.',
          'Publish the policy file at https://mta-sts.' + domain + '/.well-known/mta-sts.txt.',
          'Add a TLS-RPT record at “_smtp._tls” to receive reports.',
        ],
        extra: record(domain, 'TXT', '_smtp._tls', `v=TLSRPTv1; rua=mailto:tls-reports@${domain}`),
        alternative: null,
      },
    });
  }

  // ── Relay-side checks (public hosts: the relay is the mailbox) ──
  if (includeRelay) {
    const listed = rand() < 0.3;
    checks.push({
      id: 'blocklist',
      kind: 'blocklist',
      label: 'Sending-IP reputation',
      question: 'Are the addresses your mail leaves from on a blocklist?',
      status: listed ? 'action' : 'ok',
      lookup: { type: 'TXT', host: 'zen.spamhaus.org', name: 'zen.spamhaus.org' },
      found: listed
        ? `Listed — Spamhaus CSS, ${senderRelay(sender)} egress range`
        : 'Not listed on Spamhaus ZEN, SpamCop or Barracuda',
      expected: null,
      required: false,
      why: `Mail sent through ${relay} leaves from ${sender.senderType === 'smtp' ? 'your provider’s' : 'Brevo’s'} pool of addresses. If one of them is listed, everything you send from it is refused before the content is even read.`,
      fix: listed
        ? {
            summary:
              'One of the shared sending addresses is listed — ask for a delisting and a cleaner pool.',
            steps: [
              'Open the delisting form at spamhaus.org/lookup and paste the listed address.',
              `Tell ${sender.senderType === 'smtp' ? 'your SMTP provider' : 'Brevo support'} which address is listed; shared pools are emptied and rotated by them, not by you.`,
              'Meanwhile, lower volume and send to your most engaged recipients only, so the removal request is not undone by fresh complaints.',
            ],
            extra: null,
            alternative:
              'Shared-IP pools are outside your control. A sender on your own domain with a dedicated IP avoids this failure mode entirely.',
          }
        : null,
    });

    const ptrMismatch = rand() < 0.15;
    checks.push({
      id: 'ptr',
      kind: 'ptr',
      label: 'Reverse DNS (PTR) on the relay',
      question: 'Does the sending address resolve back to the name it announces?',
      status: ptrMismatch ? 'warn' : 'ok',
      lookup: { type: 'PTR', host: relayHost ?? relay, name: relayHost ?? relay },
      found: ptrMismatch
        ? `${relayHost ?? relay} announces a name that does not match its PTR record`
        : `${relayHost ?? relay} matches its PTR record`,
      expected: null,
      required: false,
      why: 'Many providers check that the address a server greets them from resolves back to the same name. A mismatch is a small, silent penalty on every message.',
      fix: ptrMismatch
        ? {
            summary:
              'Nickname/address mismatch on the relay — a deliverability penalty you do not control.',
            steps: [
              `Ask ${sender.senderType === 'smtp' ? 'your SMTP provider' : 'Brevo'} to align the HELO name of ${relayHost ?? relay} with its PTR record.`,
              'Sending through a domain you own keeps your reputation separate from the shared relay either way.',
            ],
            extra: null,
            alternative: null,
          }
        : null,
    });
  }

  return sortChecks(checks);
}

// ─────────────────────────────────────────────────────────────────
// Verification + health reports
// ─────────────────────────────────────────────────────────────────

/** The report the Domain tab renders: proof of ownership, then the records. */
export function buildDomainVerification(
  sender: EmailSender,
  now: Date = new Date(),
): DomainVerification {
  const domain = senderDomain(sender) ?? '';
  const publicHost = isPublicMailboxDomain(domain);
  const seed = hashSeed(`${sender.id}|${domain}`);
  const checks = buildDnsChecks(sender, { includeRelay: false });
  const ownership = checks.find((check) => check.kind === 'ownership') ?? null;
  const blockers = checks.filter((check) => check.required && check.status === 'action');
  const waiting = checks.filter((check) => check.required && check.status === 'propagating');

  const status: DomainVerification['status'] = publicHost
    ? 'unverifiable'
    : blockers.length > 0
      ? 'action_required'
      : waiting.length > 0
        ? 'pending'
        : 'verified';

  return {
    domain,
    status,
    checkedAt: now.toISOString(),
    verifiedAt:
      ownership?.status === 'ok' || status === 'verified' || status === 'pending'
        ? daysAgo(now, 9, 11)
        : null,
    token: verificationToken(seed),
    ownershipHost: ownership?.expected?.fqdn ?? `_blogforger.${domain}`,
    blockers,
    checks,
  };
}

/** 0–100, from what the checks found. Never random: the same report scores the same. */
export function mailHealthScore(checks: readonly DnsCheck[]): number {
  let score = 100;
  for (const check of checks) {
    if (check.status === 'action') score -= check.required ? 25 : 18;
    else if (check.status === 'warn') score -= 8;
    else if (check.status === 'propagating') score -= 4;
  }
  return Math.max(0, Math.min(100, score));
}

export function healthGrade(score: number): MailHealth['grade'] {
  if (score >= 90) return 'A';
  if (score >= 75) return 'B';
  if (score >= 60) return 'C';
  return 'D';
}

export function healthVerdict(score: number): string {
  if (score >= 90) return 'Public DNS says everything a receiver checks for is in place.';
  if (score >= 75)
    return 'Mostly healthy — a couple of records are leaving deliverability on the table.';
  if (score >= 60)
    return 'Deliverability is at risk: the records below should be fixed before the next campaign.';
  return 'Mail from this domain is likely to be filtered or refused. Fix these before sending again.';
}

/**
 * The report the Mail health tab renders.
 *
 * For a shared mailbox the domain's own records are read-only, so the report
 * leans on what a receiver would conclude — including the relay-side checks
 * (blocklist, reverse DNS) that decide whether the mail is even accepted.
 */
export function buildMailHealth(sender: EmailSender, now: Date = new Date()): MailHealth {
  const domain = senderDomain(sender) ?? '';
  const publicHost = isPublicMailboxDomain(domain);
  const checks = buildDnsChecks(sender, { includeRelay: true });
  const score = mailHealthScore(checks);
  return {
    domain,
    publicHost,
    provider: publicHost ? providerFacts(domain).provider : null,
    relay: senderRelay(sender),
    checkedAt: now.toISOString(),
    resolvers: ['1.1.1.1 (Cloudflare)', '8.8.8.8 (Google)', '9.9.9.9 (Quad9)'],
    score,
    grade: healthGrade(score),
    verdict: healthVerdict(score),
    checks,
    fixes: checks.filter((check) => check.fix !== null),
  };
}

// ─────────────────────────────────────────────────────────────────
// Simulated re-checks
//
// There is no resolver behind this page yet, so the two buttons on it move
// the report forward the way a real one would: a record that was missing
// comes back as "propagating" (public DNS takes minutes to hours), and one
// that was propagating comes back "ok". Same data, one state further on.
// ─────────────────────────────────────────────────────────────────

function advanceCheck(check: DnsCheck): DnsCheck {
  if (check.status !== 'action' && check.status !== 'propagating') return check;

  if (check.status === 'propagating') {
    return {
      ...check,
      status: 'ok',
      found: check.expected?.value ?? check.found,
      fix: null,
    };
  }

  // A record the user just added: resolvers answer, but not everywhere yet.
  return {
    ...check,
    status: 'propagating',
    found: check.expected?.value ?? null,
    fix: check.fix
      ? {
          ...check.fix,
          summary:
            'Record found, but resolvers are still catching up — this usually clears within a few hours.',
          steps: [
            'Nothing to add. Re-check later today; DNS changes are cached for the length of their TTL.',
          ],
          extra: null,
        }
      : null,
  };
}

/** What one "Check verification" press returns. */
export function advanceDomainVerification(
  verification: DomainVerification,
  now: Date = new Date(),
): DomainVerification {
  const checks = sortChecks(verification.checks.map(advanceCheck));
  const blockers = checks.filter((check) => check.required && check.status === 'action');
  const waiting = checks.filter((check) => check.required && check.status === 'propagating');
  const ownershipOk = checks.find((check) => check.kind === 'ownership')?.status === 'ok';

  return {
    ...verification,
    checks,
    blockers,
    status: blockers.length > 0 ? 'action_required' : waiting.length > 0 ? 'pending' : 'verified',
    checkedAt: now.toISOString(),
    verifiedAt: ownershipOk ? (verification.verifiedAt ?? now.toISOString()) : null,
  };
}

/** What one "Re-check DNS" press returns. */
export function advanceMailHealth(health: MailHealth, now: Date = new Date()): MailHealth {
  const checks = sortChecks(health.checks.map(advanceCheck));
  const score = mailHealthScore(checks);
  return {
    ...health,
    checks,
    fixes: checks.filter((check) => check.fix !== null),
    score,
    grade: healthGrade(score),
    verdict: healthVerdict(score),
    checkedAt: now.toISOString(),
  };
}
